import {
  Alert,
  Badge,
  Button,
  Column,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  MenuItemProps,
  Modal,
  Table,
} from '@odin-labs/components';
import {
  SitePermission,
  useGetJobsiteConfigSiteLayoutQuery,
  useGetSiteGatewaysLazyQuery,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { Container } from 'components/container';
import {
  NewHeader,
  classes as headerClasses,
} from 'components/header/NewHeader';
import { PlusIcon, TrashIcon } from 'components/icons';
import { LoadingError } from 'components/loadingError';
import { CardTitle } from 'components/reactstrap';
import { useRoutedTabs } from 'components/tabs';
import {
  EntryEditor,
  OUTSIDE_OPTION_VALUE,
  SideReader,
  ZoneEditor,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/components';
import {
  draftTagBadge,
  seedHelp,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/labels';
import {
  CURRENT_VERSION,
  DesignerTab,
  DRAFT_VERSION,
  formatPublishedAt,
  siteLayoutDesignerPath,
  siteLayoutTabPath,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/layoutVersions';
import {
  entryColumnsWithActions,
  LayoutNameCell,
  LayoutRowTag,
  toEntryRows,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/layoutTables';
import {
  CannotRemoveZoneModal,
  DiscardDraftModal,
  PublishDraftModal,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/modals';
import { useLayoutSelection } from 'containers/jobsiteConfiguration/tabs/siteLayout/useLayoutSelection';
import { JobsiteFeatureFlagsProvider, useFlags } from 'featureFlags';
import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  DraftTag,
  draftTagOf,
  effectiveRevision,
  entriesFacingZone,
  EntrySide,
  GatewayDestination,
  Layout,
  publishChecks,
  refusalText,
  SiteLayoutError,
  SiteLayoutRefusedError,
  SiteLayoutRevision,
  useSiteLayout,
  useSiteReaders,
} from 'siteLayout';
import { useSitePermissions } from 'sitePermissions/useSitePermissions';
import { pluralize } from 'utils/pluralize';
import { AssignReaderStep } from './AssignReaderStep';
import { GatewayDestinationsModal } from './GatewayDestinationsModal';
import { SeedRequiredModal } from './SeedRequiredModal';
import {
  blankEntryForm,
  blankZoneForm,
  checkEntryForm,
  checkZoneForm,
  EntryForm,
  entryFormFrom,
  entryOfForm,
  FormCheck,
  layoutWithEntryForm,
  layoutWithZoneForm,
  rebaseEntryForm,
  rebaseZoneForm,
  sameEntryForm,
  sameZoneForm,
  saveEntryForm,
  saveZoneForm,
  withoutReadersPlacedElsewhere,
  ZoneForm,
  zoneFormFrom,
  zoneOfForm,
} from './layoutForms';

type OpenModal = 'publish' | 'discard' | null;

const EMPTY_LAYOUT: Layout = { zones: [], entries: [], readerAssignments: [] };

const classes = {
  container: 'odin-h-full',
  topRow:
    'odin-flex odin-flex-shrink-0 odin-items-center odin-justify-between odin-gap-3',
  header: 'odin-flex-shrink-0',
  // Where the settings page has its Back link: the path to this page.
  crumbs:
    'odin-flex odin-items-center odin-gap-x-2 odin-text-sm odin-text-gray-500',
  crumbLink: 'odin-font-medium odin-text-odin-primary hover:odin-underline',
  crumbSep: 'odin-text-gray-300',
  crumbCurrent: 'odin-font-medium odin-text-gray-900',
  eyebrow:
    'odin-flex odin-flex-wrap odin-items-center odin-gap-x-2 odin-gap-y-1 odin-text-sm odin-text-gray-500',
  version: 'odin-font-medium odin-text-gray-900',
  actions: 'odin-flex odin-items-center odin-gap-2',
  tabPage: 'odin-flex-1 odin-overflow-y-auto odin-bg-white',
  page: 'odin-flex odin-flex-col odin-gap-3 odin-py-6',
  pageActions: 'odin-flex odin-justify-end odin-gap-2',
  empty:
    'odin-rounded-lg odin-border odin-border-gray-200 odin-bg-white odin-p-10 odin-text-center odin-text-sm odin-text-gray-500',
  note: 'odin-text-xs odin-text-gray-500',
  denied: 'odin-p-6 odin-text-sm odin-text-gray-500',
  drawerTitle: 'odin-flex odin-items-center odin-gap-3',
  drawerTitleText: 'odin-min-w-0 odin-truncate',
  drawerTag: 'odin-flex-shrink-0',
};

/**
 * A drawer's title with the item's draft tag on the same line. The tag and
 * the title, which follows the name as it is committed, then never move the
 * form below: a tag on a line of its own pushed the form down as a renamed
 * field was left, so the press on Save or Assign reader that left it was
 * released elsewhere and lost. A long name is cut short on the line for the
 * same reason; the Name field shows it whole.
 *
 * `Modal` (@odin-labs/components 2026.7.0) types `title` as a string but
 * renders it as its heading's content, and its header has no other place for
 * the tag; the cast hands it the line as that content. Drop the cast if
 * `title` comes to accept a node.
 */
const drawerTitle = (text: string, tag: DraftTag): string => {
  const badge = draftTagBadge(tag);
  return (
    <span className={classes.drawerTitle}>
      <span className={classes.drawerTitleText}>{text}</span>
      {badge && (
        <span className={classes.drawerTag}>
          <Badge text={badge.text} color={badge.color} />
        </span>
      )}
    </span>
  ) as unknown as string;
};

type ZoneRow = {
  id: string;
  name: string;
  tag: LayoutRowTag;
  entries: number;
  /** Row actions, present only while editing the draft. */
  menuItems: MenuItemProps[];
};

const zoneColumns: Column<ZoneRow>[] = [
  { id: 'name', Header: 'Name', accessor: 'name', Cell: LayoutNameCell },
  {
    id: 'entries',
    Header: 'Entries',
    accessor: (row: ZoneRow): string => String(row.entries),
  },
  {
    id: 'actions',
    Header: '',
    Cell: DropdownButtonCell,
    componentProps: (row: ZoneRow): DropdownButtonCellComponentProps => ({
      cellAlignment: 'right',
      menuItems: row.menuItems,
    }),
  },
];

const tabsConfig = [
  { name: 'zones', text: 'Zones', relativePath: '/zones' },
  { name: 'entries', text: 'Entries', relativePath: '/entries' },
];

type RouteParams = { jobsiteId: string; version: string; tab?: DesignerTab };

function SiteLayoutDesigner({
  jobsiteId,
  version,
  tab,
}: RouteParams): React.ReactElement {
  const history = useHistory();
  const currentTab: DesignerTab = tab === 'entries' ? 'entries' : 'zones';
  const goTo = React.useCallback(
    (next: string, replace = false): void => {
      const path = siteLayoutDesignerPath(jobsiteId, next, currentTab);
      if (replace) history.replace(path);
      else history.push(path);
    },
    [history, jobsiteId, currentTab],
  );
  const { tabs, currentTab: routedTab } = useRoutedTabs({
    tabsConfig,
    baseRoute: siteLayoutDesignerPath(jobsiteId, version),
  });

  const { data: jobsiteData } = useGetJobsiteConfigSiteLayoutQuery({
    variables: { jobsiteId },
    fetchPolicy: 'no-cache',
  });
  const jobsiteName = jobsiteData?.getJobsite?.name;
  // Only a user who may manage the site's layout edits it; anyone who may
  // view it sees every version, the draft included, read-only.
  const canManage = useSitePermissions(jobsiteId).can(
    SitePermission.ManageSiteLayout,
  );

  const {
    state,
    error: layoutError,
    actions,
    stalled,
  } = useSiteLayout(jobsiteId);
  const {
    readers: siteReaders,
    failed: readersFailed,
    refetch: refetchReaders,
  } = useSiteReaders(jobsiteId);

  const { selection, select, clear } = useLayoutSelection();
  // The entry drawer's assign step: which crossing is taking a reader, and
  // the reader picked so far.
  const [pickerSideId, setPickerSideId] = React.useState<string | null>(null);
  const [assignReaderId, setAssignReaderId] = React.useState<string | null>(
    null,
  );
  // The open drawer's edits, written to the draft on Save.
  const [zoneForm, setZoneForm] = React.useState<ZoneForm | null>(null);
  const [entryForm, setEntryForm] = React.useState<EntryForm | null>(null);
  // The stored zone or entry the open form was built from, and a count of
  // the saves that failed: each failure read the draft again, and the form
  // is rebased onto it.
  const zoneFormBase = React.useRef<ZoneForm | null>(null);
  const entryFormBase = React.useRef<EntryForm | null>(null);
  const [failedSaves, setFailedSaves] = React.useState(0);
  // Set by each Save tried and cleared as a drawer opens; while it is null, a
  // drawer's field shows its error only once touched.
  const [formCheck, setFormCheck] = React.useState<FormCheck | null>(null);
  const [openModal, setOpenModal] = React.useState<OpenModal>(null);
  // Seeding from the site's gateways: those it needs a zone for, and the
  // zone chosen for each.
  const [seedGatewayIds, setSeedGatewayIds] = React.useState<string[] | null>(
    null,
  );
  const [seedDestinations, setSeedDestinations] = React.useState<
    Record<string, string>
  >({});
  const [seedError, setSeedError] = React.useState<string | null>(null);
  const [loadGateways] = useGetSiteGatewaysLazyQuery({
    fetchPolicy: 'no-cache',
  });
  // The gateways a publish was refused for until they are seeded.
  const [seedRequiredGatewayIds, setSeedRequiredGatewayIds] = React.useState<
    string[] | null
  >(null);
  const [blockedZone, setBlockedZone] = React.useState<{
    zoneName: string;
    entryNames: string[];
  } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const running = React.useRef(false);

  // ---- which version the route names
  const revisions = state?.revisions ?? [];
  const draft = state?.draft ?? null;
  const effective = effectiveRevision(revisions);
  const isDraft = draft !== null;
  const noLayout = !effective && !isDraft;
  const viewingDraft = version === DRAFT_VERSION && isDraft;
  const editingDraft = viewingDraft && canManage;
  let viewingRevision: SiteLayoutRevision | null = null;
  if (version === CURRENT_VERSION) viewingRevision = effective;
  else if (version !== DRAFT_VERSION)
    viewingRevision =
      revisions.find((revision) => revision.id === version) ?? null;
  const viewingCurrent =
    viewingRevision !== null && viewingRevision.id === effective?.id;
  let layout: Layout = EMPTY_LAYOUT;
  if (viewingDraft && draft) layout = draft;
  else if (viewingRevision) layout = viewingRevision;
  // The draft route without a draft, or a revision route without that
  // revision, lands on what there is.
  const missing =
    state !== null &&
    !noLayout &&
    ((version === DRAFT_VERSION && !isDraft) ||
      (version !== DRAFT_VERSION && !viewingRevision));
  React.useEffect(() => {
    if (!missing) return;
    if (version === DRAFT_VERSION) goTo(CURRENT_VERSION, true);
    else if (isDraft) goTo(DRAFT_VERSION, true);
    else goTo(CURRENT_VERSION, true);
  }, [missing, version, isDraft, goTo]);

  // Until the site's readers load, they are unknown, not none.
  const placeableReaders = siteReaders?.filter(({ missing: gone }) => !gone);
  const readersById = React.useMemo(
    () =>
      new Map<string, SideReader>(
        siteReaders
          ? siteReaders.map((reader) => [reader.id, reader])
          : layout.readerAssignments.map(({ readerDeviceId }) => [
              readerDeviceId,
              {
                id: readerDeviceId,
                name: 'Reader',
                portLabel: 'Loading readers…',
                missing: true,
              },
            ]),
      ),
    [siteReaders, layout.readerAssignments],
  );
  const readerNameOf = (readerDeviceId: string): string =>
    readersById.get(readerDeviceId)?.name ?? 'Unknown reader';

  // ---- commands, each reported through the alert service
  const run = async (
    work: () => Promise<void>,
    success?: string,
    /** Takes a refusal the server made, instead of the alert, so a drawer can keep its input. */
    onRefused?: (error: SiteLayoutRefusedError) => void,
  ): Promise<boolean> => {
    if (running.current || stalled) return false;
    running.current = true;
    setBusy(true);
    try {
      await work();
      if (success) AlertService.alert('success', 'Layout', success);
      return true;
    } catch (error) {
      if (onRefused && error instanceof SiteLayoutRefusedError) {
        onRefused(error);
        return false;
      }
      const message =
        error instanceof SiteLayoutError
          ? error.message
          : (error as Error).message;
      AlertService.alert('danger', 'Something went wrong!', message);
      return false;
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  // A refusal the server made, shown at the top of the drawer over the input
  // it refused, which stays. No side is flagged: a flagged side means it
  // needs a reader, and the server's side refusals are about what it faces.
  // A draft that changed since keeps the input too, and the state is read
  // again behind it; the form is then rebased onto it (below).
  const refusalCheck = (error: SiteLayoutRefusedError): FormCheck => ({
    errors: error.refusals.map(refusalText),
    invalidSideIds: [],
  });

  const editLayout = (fromRevisionId?: string, fromNumber?: number): void => {
    let success: string | undefined;
    if (fromRevisionId && fromNumber !== undefined)
      success = `Draft started from version ${fromNumber}. This is a copy, not a rollback; the current layout stays effective until you publish.`;
    else if (effective)
      success = `Draft opened from version ${effective.number}. Nothing changes on site until you publish.`;
    run(async () => {
      await actions.openDraft(fromRevisionId);
      clear();
      goTo(DRAFT_VERSION);
    }, success);
  };

  const addZone = (): void => {
    clear();
    setFormCheck(null);
    setZoneForm(blankZoneForm());
  };

  const addEntry = (): void => {
    clear();
    setFormCheck(null);
    setPickerSideId(null);
    setEntryForm(blankEntryForm(layout));
  };

  const closeDrawer = (): void => {
    clear();
    setZoneForm(null);
    setEntryForm(null);
    setFormCheck(null);
    setPickerSideId(null);
  };

  const saveZone = (): void => {
    if (!zoneForm) return;
    const check = checkZoneForm(layout, zoneForm);
    setFormCheck(check);
    if (check.errors.length) return;
    run(
      async () => {
        await saveZoneForm(actions, zoneForm);
        closeDrawer();
      },
      `${zoneForm.name.trim()} saved.`,
      (error) => setFormCheck(refusalCheck(error)),
    ).then((saved) => {
      if (!saved) setFailedSaves((count) => count + 1);
    });
  };

  const saveEntry = (): void => {
    if (!entryForm) return;
    const check = checkEntryForm(layout, entryForm);
    setFormCheck(check);
    if (check.errors.length) return;
    const name = entryForm.name.trim();
    run(
      async () => {
        const { movedReaders } = await saveEntryForm(actions, entryForm);
        // A reader placed on another entry, by this edit or by someone
        // meanwhile, moved here: say from where.
        if (movedReaders.length)
          AlertService.alert(
            'info',
            'Readers moved',
            movedReaders
              .map(
                ({ readerDeviceId, fromEntryId }) =>
                  `${readerNameOf(readerDeviceId)} moved to ${name} from ${
                    layout.entries.find(({ id }) => id === fromEntryId)?.name ||
                    'another entry'
                  }.`,
              )
              .join(' '),
            6000,
          );
        closeDrawer();
      },
      `${name} saved.`,
      (error) => setFormCheck(refusalCheck(error)),
    ).then((saved) => {
      if (!saved) setFailedSaves((count) => count + 1);
    });
  };

  const setEntrySide = (side: EntrySide, patch: Partial<EntrySide>): void =>
    setEntryForm((form) => {
      if (!form) return form;
      const key = side.position === 'A' ? 'sideA' : 'sideB';
      const readersKey = side.position === 'A' ? 'readersA' : 'readersB';
      // A crossing set to None cannot have readers.
      const readers =
        patch.enforcement === 'NOT_TRACKED' ? [] : form[readersKey];
      return {
        ...form,
        [key]: { ...form[key], ...patch },
        [readersKey]: readers,
      };
    });

  const setFaces = (side: EntrySide, value: string): void =>
    setEntrySide(
      side,
      value === OUTSIDE_OPTION_VALUE
        ? { kind: 'OUTSIDE', zoneId: null }
        : { kind: 'ZONE', zoneId: value },
    );

  const placeReader = (side: EntrySide, readerDeviceId: string): void => {
    setEntryForm((form) => {
      if (!form) return form;
      const key = side.position === 'A' ? 'readersA' : 'readersB';
      return { ...form, [key]: [...form[key], readerDeviceId] };
    });
    setPickerSideId(null);
    setAssignReaderId(null);
  };

  const unplaceReader = (readerDeviceId: string): void =>
    setEntryForm((form) =>
      form
        ? {
            ...form,
            readersA: form.readersA.filter((id) => id !== readerDeviceId),
            readersB: form.readersB.filter((id) => id !== readerDeviceId),
          }
        : form,
    );

  const removeZone = (zoneId: string): void => {
    const zone = layout.zones.find((candidate) => candidate.id === zoneId);
    const facing = entriesFacingZone(layout, zoneId);
    if (facing.length) {
      setBlockedZone({
        zoneName: zone?.name ?? '',
        entryNames: facing.map((entry) => entry.name || 'Unnamed entry'),
      });
      return;
    }
    run(async () => {
      await actions.removeZone(zoneId);
      clear();
    }, `${zone?.name || 'Zone'} removed from the draft.`);
  };

  // A seed's refusal, naming the gateways a field refusal is about; in the
  // server's words alone when the gateways cannot be read.
  const seedRefusalText = async (
    error: SiteLayoutRefusedError,
  ): Promise<string> => {
    if (
      !error.refusals.some(
        (refusal) => refusal.code === 'FIELD' && refusal.gatewayIds?.length,
      )
    )
      return error.message;
    const { data } = await loadGateways({ variables: { siteId: jobsiteId } });
    if (!data?.getJobsite) return error.message;
    const names = new Map(
      data.getJobsite.jobsiteGateways.edges.map(({ node }) => [
        node.id,
        node.name,
      ]),
    );
    return error.refusals
      .map((refusal) =>
        refusal.code === 'FIELD' && refusal.gatewayIds?.length
          ? `${refusal.message}. ${
              refusal.gatewayIds.length === 1 ? 'Gateway' : 'Gateways'
            }: ${refusal.gatewayIds
              .map((id) => names.get(id) ?? 'Gateway')
              .join(', ')}.`
          : refusalText(refusal),
      )
      .join(' ');
  };

  // A draft with one zone is seeded at once; with several, the seed asks for
  // each gateway's zone, and is sent again with them.
  const seedEntries = (destinations?: GatewayDestination[]): void => {
    run(
      async () => {
        const { seededEntryIds } = await actions.seedEntriesFromGateways(
          destinations,
        );
        setSeedGatewayIds(null);
        AlertService.alert(
          'success',
          'Layout',
          seededEntryIds.length
            ? `${pluralize(
                seededEntryIds.length,
                'entry',
                'entries',
              )} added from gateways.`
            : 'Every gateway already has an entry.',
        );
      },
      undefined,
      (error) => {
        const needed = error.refusals.find(
          (refusal) => refusal.code === 'SEED_NEEDS_DESTINATION',
        );
        if (needed?.code === 'SEED_NEEDS_DESTINATION' && layout.zones.length) {
          setSeedGatewayIds(needed.gatewayIds);
          setSeedError(destinations ? error.message : null);
        } else if (seedGatewayIds) seedRefusalText(error).then(setSeedError);
        else
          seedRefusalText(error).then((message) =>
            AlertService.alert('danger', 'Something went wrong!', message),
          );
      },
    );
  };

  const removeEntry = (entryId: string, entryName: string): void => {
    const readerCount = layout.readerAssignments.filter(
      (assignment) => assignment.entryId === entryId,
    ).length;
    run(async () => {
      await actions.removeEntry(entryId);
      clear();
    }, `${entryName || 'Entry'} removed from the draft.${readerCount ? ` ${pluralize(readerCount, 'reader')} removed from this draft.` : ''}`);
  };

  // A publish refused until the site's gateways are seeded offers the seed
  // for them; once seeded, the draft is published again from the header.
  const publish = (): void => {
    const number = draft?.number;
    run(
      async () => {
        await actions.publishDraft();
        await refetchReaders();
        setOpenModal(null);
        clear();
        goTo(CURRENT_VERSION, true);
      },
      `Version ${number} published.`,
      (error) => {
        const seedRequired = error.refusals.find(
          (refusal) => refusal.code === 'SEED_REQUIRED',
        );
        if (seedRequired?.code === 'SEED_REQUIRED') {
          setOpenModal(null);
          setSeedRequiredGatewayIds(seedRequired.gatewayIds);
        } else
          AlertService.alert('danger', 'Something went wrong!', error.message);
      },
    );
  };

  const discard = (): void => {
    run(async () => {
      await actions.discardDraft();
      setOpenModal(null);
      clear();
      goTo(CURRENT_VERSION, true);
    }, 'Draft discarded.');
  };

  // ---- header: which version this is, and what can be done with it
  const blocked = busy || stalled;
  let eyebrow: React.ReactNode = null;
  let headerActions: React.ReactNode = null;
  if (viewingDraft) {
    eyebrow = (
      <>
        <span className={classes.version}>Version {draft?.number}</span>
        <Badge text="Unpublished" color="purple" />
      </>
    );
    headerActions = editingDraft && (
      <>
        <Button
          text="Discard draft"
          size="sm"
          theme="white"
          disabled={blocked}
          onClick={() => setOpenModal('discard')}
        />
        <Button
          text="Publish"
          size="sm"
          disabled={blocked}
          onClick={() => setOpenModal('publish')}
        />
      </>
    );
  } else if (viewingRevision) {
    eyebrow = (
      <>
        <span className={classes.version}>
          Version {viewingRevision.number}
        </span>
        {viewingCurrent && <Badge text="Published" color="green" />}
        <span>
          {viewingCurrent
            ? `Published ${formatPublishedAt(viewingRevision.publishedAt)}${
                viewingRevision.publishedBy
                  ? ` by ${viewingRevision.publishedBy}`
                  : ''
              }`
            : `Published ${formatPublishedAt(
                viewingRevision.publishedAt,
              )} to ${formatPublishedAt(viewingRevision.closedAt)}`}
        </span>
        {isDraft && viewingCurrent && (
          <span>· A draft has unpublished changes</span>
        )}
      </>
    );
    headerActions = (
      <>
        {!viewingCurrent && (
          <Button
            text="View current"
            size="sm"
            theme="white"
            onClick={() => goTo(CURRENT_VERSION)}
          />
        )}
        {isDraft && canManage && (
          <Button
            text="Open draft"
            size="sm"
            onClick={() => goTo(DRAFT_VERSION)}
          />
        )}
        {!isDraft && viewingCurrent && canManage && (
          <Button
            text="Edit layout"
            size="sm"
            disabled={blocked}
            onClick={() => editLayout()}
          />
        )}
        {!isDraft && !viewingCurrent && canManage && (
          <Button
            text="Start a draft from this version"
            size="sm"
            disabled={blocked}
            onClick={() =>
              editLayout(viewingRevision.id, viewingRevision.number)
            }
          />
        )}
      </>
    );
  } else if (noLayout) {
    eyebrow = <span>No layout yet</span>;
    headerActions = canManage && (
      <Button
        text="Edit layout"
        size="sm"
        disabled={blocked}
        onClick={() => editLayout()}
      />
    );
  }

  // ---- tables
  const canAddZone = editingDraft;
  const canAddEntry = editingDraft && layout.zones.length > 0;
  const removedNames =
    editingDraft && effective
      ? [
          ...effective.zones.filter(
            (zone) =>
              !layout.zones.some((candidate) => candidate.id === zone.id),
          ),
          ...effective.entries.filter(
            (entry) =>
              !layout.entries.some((candidate) => candidate.id === entry.id),
          ),
        ].map((item) => item.name)
      : [];
  const zoneRows = React.useMemo<ZoneRow[]>(
    () =>
      layout.zones.map((zone) => ({
        id: zone.id,
        name: zone.name,
        tag: editingDraft
          ? draftTagBadge(draftTagOf(effective, 'zones', zone))
          : null,
        entries: entriesFacingZone(layout, zone.id).length,
        menuItems: editingDraft
          ? [
              {
                icon: TrashIcon,
                text: 'Remove',
                theme: 'danger',
                onClick: (): void => removeZone(zone.id),
              },
            ]
          : [],
      })),
    // removeZone is recreated each render; the rows only need the layout.
    [layout, editingDraft, effective],
  );
  const entryRows = React.useMemo(
    () =>
      toEntryRows(
        layout,
        layout.entries,
        (entry) =>
          editingDraft
            ? draftTagBadge(draftTagOf(effective, 'entries', entry))
            : null,
        (entry) =>
          editingDraft
            ? [
                {
                  icon: TrashIcon,
                  text: 'Remove',
                  theme: 'danger',
                  onClick: (): void => removeEntry(entry.id, entry.name),
                },
              ]
            : [],
      ),
    // removeEntry is recreated each render; the rows only need the layout.
    [layout, editingDraft, effective],
  );

  // A selected zone or entry opens its drawer with a fresh form; a form with
  // no id is a new one and is not in the URL.
  const layoutLoaded = state !== null;
  React.useEffect(() => {
    setFormCheck(null);
    setPickerSideId(null);
    if (selection?.type === 'zone') {
      const zone = layout.zones.find(
        (candidate) => candidate.id === selection.id,
      );
      zoneFormBase.current = zone ? zoneFormFrom(zone) : null;
      setZoneForm(zoneFormBase.current);
      setEntryForm(null);
    } else if (selection?.type === 'entry') {
      const entry = layout.entries.find(
        (candidate) => candidate.id === selection.id,
      );
      entryFormBase.current = entry ? entryFormFrom(layout, entry) : null;
      setEntryForm(entryFormBase.current);
      setZoneForm(null);
    } else {
      setZoneForm((form) => (form?.id ? null : form));
      setEntryForm((form) => (form?.id ? null : form));
    }
    // Only a change of selection, or the layout arriving, rebuilds the forms;
    // a refetch after a save must not wipe edits in progress.
  }, [selection?.type, selection?.id, layoutLoaded]);

  // A save that failed read the draft again, whatever the failure. If
  // someone changed the open zone or entry meanwhile, the next save must
  // carry their changes rather than undo them unseen: the form is rebased
  // onto the draft as it is now, keeping the user's edits to what they left
  // alone, and says so. A zone or entry they removed closes its drawer; a
  // new entry lets go of a reader they placed elsewhere. A draft published
  // or discarded meanwhile leaves the form alone: the page goes to the
  // current version.
  React.useEffect(() => {
    if (!failedSaves || !draft) return;
    const zoneBase = zoneFormBase.current;
    const entryBase = entryFormBase.current;
    const noteChanged = (text: string): void =>
      setFormCheck((check) => ({
        errors: [...(check?.errors ?? []), text],
        invalidSideIds: check?.invalidSideIds ?? [],
      }));
    const noteRemoved = (name: string): void => {
      closeDrawer();
      AlertService.alert(
        'info',
        'Layout',
        `${name} was removed from the draft since you opened it.`,
      );
    };
    if (zoneForm?.id && zoneBase?.id === zoneForm.id) {
      const zone = layout.zones.find(({ id }) => id === zoneForm.id);
      if (!zone) noteRemoved(zoneBase.name || 'This zone');
      else {
        const fresh = zoneFormFrom(zone);
        const rebased = rebaseZoneForm(zoneForm, zoneBase, fresh);
        zoneFormBase.current = fresh;
        if (!sameZoneForm(rebased, zoneForm)) {
          setZoneForm((form) => form && rebaseZoneForm(form, zoneBase, fresh));
          noteChanged(
            'Someone changed this zone since you opened it. It now shows their changes, and keeps your edits to anything they left alone.',
          );
        }
      }
    } else if (entryForm?.id && entryBase?.id === entryForm.id) {
      const entry = layout.entries.find(({ id }) => id === entryForm.id);
      if (!entry) noteRemoved(entryBase.name || 'This entry');
      else {
        const fresh = entryFormFrom(layout, entry);
        const rebased = rebaseEntryForm(layout, entryForm, entryBase, fresh);
        entryFormBase.current = fresh;
        if (!sameEntryForm(rebased, entryForm)) {
          setEntryForm(
            (form) => form && rebaseEntryForm(layout, form, entryBase, fresh),
          );
          noteChanged(
            'Someone changed this entry, or placed one of its readers elsewhere, since you opened it. It now shows their changes, and keeps your edits to anything they left alone.',
          );
        }
      }
    } else if (entryForm && !entryForm.id) {
      const kept = withoutReadersPlacedElsewhere(layout, entryForm);
      if (!sameEntryForm(kept, entryForm)) {
        setEntryForm(
          (form) => form && withoutReadersPlacedElsewhere(layout, form),
        );
        noteChanged(
          'Someone placed a reader you chose on another entry since you opened this one, so it is no longer on this entry.',
        );
      }
    }
    // Only a failed save rebases the form; the layout it read is this render's.
  }, [failedSaves]);

  const zoneLayout = zoneForm ? layoutWithZoneForm(layout, zoneForm) : layout;
  const formZone = zoneForm ? zoneOfForm(zoneForm) : null;
  const entryLayout = entryForm
    ? layoutWithEntryForm(layout, entryForm, readerNameOf)
    : layout;
  const formEntry = entryForm ? entryOfForm(entryForm) : null;
  const drawerEditable = editingDraft && !blocked;
  // The crossing the assign step is open for, if any; the pool is every site
  // reader not placed anywhere in the layout, counting this drawer's edits.
  const assigningSide =
    formEntry && pickerSideId
      ? [formEntry.sideA, formEntry.sideB].find(
          (side) => side.id === pickerSideId,
        ) ?? null
      : null;
  const placedReaderIds = new Set(
    entryLayout.readerAssignments.map(
      (assignment) => assignment.readerDeviceId,
    ),
  );
  const unplacedReaders = [...readersById.values()].filter(
    (reader) => !placedReaderIds.has(reader.id),
  );
  const leaveEntryDrawerStep = (): void => {
    if (pickerSideId) {
      setPickerSideId(null);
      setAssignReaderId(null);
    } else closeDrawer();
  };
  let entryModalActions: Partial<React.ComponentProps<typeof Modal>> = {
    actionsPanel: null,
    cancelText: 'Close',
  };
  if (assigningSide) {
    entryModalActions = {
      // Writes nothing: the entry's Save does, once back on the entry.
      actionText: 'Select',
      actionButtonEnabled: Boolean(assignReaderId),
      onAction: (): void => {
        if (assignReaderId) placeReader(assigningSide, assignReaderId);
      },
      cancelText: 'Back',
      closeButtonBehavior: 'dismiss',
    };
  } else if (drawerEditable) {
    entryModalActions = {
      actionText: 'Save',
      actionButtonWithSpinner: busy,
      onAction: saveEntry,
      cancelText: 'Cancel',
      closeButtonBehavior: 'dismiss',
    };
  }
  let entryDrawerTitle = 'New entry';
  if (assigningSide) entryDrawerTitle = 'Assign reader';
  else if (entryForm?.id) entryDrawerTitle = entryForm.name || 'Unnamed entry';
  let zoneModalActions: Partial<React.ComponentProps<typeof Modal>> = {
    actionsPanel: null,
    cancelText: 'Close',
  };
  if (drawerEditable) {
    zoneModalActions = {
      actionText: 'Save',
      actionButtonWithSpinner: busy,
      onAction: saveZone,
      cancelText: 'Cancel',
      closeButtonBehavior: 'dismiss',
    };
  }
  let zoneDrawerTitle = 'New zone';
  if (zoneForm?.id) zoneDrawerTitle = zoneForm.name || 'Unnamed zone';
  let zoneTag: DraftTag = null;
  if (editingDraft && formZone)
    zoneTag = zoneForm?.id ? draftTagOf(effective, 'zones', formZone) : 'NEW';
  let entryTag: DraftTag = null;
  if (editingDraft && formEntry)
    entryTag = entryForm?.id
      ? draftTagOf(effective, 'entries', formEntry)
      : 'NEW';
  const isEmptyLayout = !layout.zones.length && !layout.entries.length;

  const layoutChecks = editingDraft
    ? publishChecks(
        layout,
        (placeableReaders ?? []).map((reader) => ({
          id: reader.id,
          name: reader.name,
        })),
      )
    : { blocking: [], warnings: [], canPublish: false };
  const checks =
    editingDraft && !placeableReaders
      ? {
          ...layoutChecks,
          warnings: [
            ...layoutChecks.warnings,
            {
              text: "The site's readers have not loaded, so readers not placed on any entry cannot be checked.",
            },
          ],
        }
      : layoutChecks;

  if (layoutError) return <LoadingError error={layoutError.message} />;
  if (!state || missing) return <LoadingError loading />;

  const headerTitle = (
    <CardTitle as="h1" className={headerClasses.title}>
      Site layout
    </CardTitle>
  );
  const headerSubTitle = <div className={classes.eyebrow}>{eyebrow}</div>;

  let emptyText: string | null = null;
  if (isEmptyLayout)
    emptyText = editingDraft
      ? 'This site has no layout yet. Add your first zone.'
      : 'This site has no layout yet. Edit the layout to add zones and entries.';

  return (
    <Container className={classes.container}>
      <div className={classes.topRow}>
        <nav className={classes.crumbs} aria-label="Breadcrumb">
          <Link to={`/jobsite/${jobsiteId}`} className={classes.crumbLink}>
            {jobsiteName ?? 'Site'}
          </Link>
          <span className={classes.crumbSep}>/</span>
          <Link to={siteLayoutTabPath(jobsiteId)} className={classes.crumbLink}>
            Settings
          </Link>
          <span className={classes.crumbSep}>/</span>
          <span className={classes.crumbCurrent}>Site layout</span>
        </nav>
        <div className={classes.actions}>{headerActions}</div>
      </div>
      <NewHeader
        className={classes.header}
        title={headerTitle}
        subTitle={headerSubTitle}
        tabsProps={{ tabs, currentTab: routedTab }}
        actionsProps={{ headerActions: [] }}
      />
      {stalled && (
        <Alert
          type="warn"
          text="A change is still waiting on the server. Refresh the page before making more changes."
        />
      )}

      <div className={classes.tabPage}>
        {currentTab === 'zones' ? (
          <section className={classes.page} aria-label="Zones">
            {emptyText && <div className={classes.empty}>{emptyText}</div>}
            {editingDraft && (
              <div className={classes.pageActions}>
                <Button
                  icon={PlusIcon}
                  text="Add zone"
                  size="sm"
                  theme="white"
                  disabled={!canAddZone || blocked}
                  onClick={addZone}
                />
              </div>
            )}
            <Table
              columns={zoneColumns}
              data={zoneRows}
              noResultsText="No zones"
              onRowClick={({ data: row }) => select('zone', row.id)}
              remote
              disableGlobalFilter
              disableSortBy
            />
          </section>
        ) : (
          <section className={classes.page} aria-label="Entries">
            {emptyText && <div className={classes.empty}>{emptyText}</div>}
            {editingDraft && (
              <div className={classes.pageActions}>
                <span
                  title={layout.zones.length ? undefined : 'Add a zone first'}
                >
                  <Button
                    icon={PlusIcon}
                    text="Add entry"
                    size="sm"
                    theme="white"
                    disabled={!canAddEntry || blocked}
                    onClick={addEntry}
                  />
                </span>
                <span
                  title={layout.zones.length ? seedHelp : 'Add a zone first'}
                >
                  <Button
                    icon={PlusIcon}
                    text="Add entries from gateways"
                    size="sm"
                    theme="white"
                    disabled={!canAddEntry || blocked}
                    onClick={() => {
                      setSeedDestinations({});
                      seedEntries();
                    }}
                  />
                </span>
              </div>
            )}
            <Table
              columns={entryColumnsWithActions}
              data={entryRows}
              noResultsText="No entries"
              onRowClick={({ data: row }) => select('entry', row.id)}
              remote
              disableGlobalFilter
              disableSortBy
            />
          </section>
        )}
        {removedNames.length > 0 && (
          <p className={classes.note}>
            Removed in this draft: {removedNames.join(', ')}
          </p>
        )}
      </div>

      <Modal
        open={Boolean(zoneForm)}
        // The modal calls this for Cancel and the close icon alike.
        setOpen={(open) => !open && closeDrawer()}
        variant="drawer"
        size="md"
        title={drawerTitle(zoneDrawerTitle, zoneTag)}
        {...zoneModalActions}
      >
        {zoneForm && formZone && (
          <ZoneEditor
            layout={zoneLayout}
            zone={formZone}
            tag={zoneTag}
            readOnly={!drawerEditable}
            editingDraft={editingDraft}
            effective={effective}
            showHeading={false}
            errors={formCheck?.errors ?? []}
            submitted={formCheck !== null}
            onRename={(name) =>
              setZoneForm((form) => (form ? { ...form, name } : form))
            }
            onSelectEntry={(entryId) => select('entry', entryId)}
          />
        )}
      </Modal>
      <Modal
        open={Boolean(entryForm)}
        // Back, Cancel and the close icon all land here, each stepping back
        // one level.
        setOpen={(open) => !open && leaveEntryDrawerStep()}
        variant="drawer"
        size="md"
        title={drawerTitle(entryDrawerTitle, assigningSide ? null : entryTag)}
        {...entryModalActions}
      >
        {entryForm && assigningSide && (
          <AssignReaderStep
            pool={siteReaders && unplacedReaders}
            noSiteReaders={siteReaders?.length === 0}
            failed={readersFailed}
            onRetry={refetchReaders}
            selectedId={assignReaderId}
            onSelect={setAssignReaderId}
            busy={busy}
          />
        )}
        {entryForm && formEntry && !assigningSide && (
          <EntryEditor
            layout={entryLayout}
            entry={formEntry}
            tag={entryTag}
            readOnly={!drawerEditable}
            editingDraft={editingDraft}
            showHeading={false}
            readersById={readersById}
            errors={formCheck?.errors ?? []}
            invalidSideIds={formCheck?.invalidSideIds ?? []}
            submitted={formCheck !== null}
            onRename={(name) =>
              setEntryForm((form) => (form ? { ...form, name } : form))
            }
            onSetFaces={setFaces}
            onSetEnforcement={(side, enforcement) =>
              setEntrySide(side, { enforcement })
            }
            onAssignReader={(side) => {
              setAssignReaderId(null);
              setPickerSideId(side.id);
            }}
            onRemoveReader={unplaceReader}
          />
        )}
      </Modal>

      <PublishDraftModal
        isOpen={openModal === 'publish'}
        siteName={jobsiteName ?? ''}
        nextRevisionNumber={draft?.number ?? 0}
        currentRevisionNumber={effective?.number ?? null}
        checks={checks}
        publishing={busy}
        onCancel={() => setOpenModal(null)}
        onPublish={publish}
        onOpenItem={(type, id) => {
          setOpenModal(null);
          select(type, id);
        }}
      />
      <SeedRequiredModal
        siteId={jobsiteId}
        gatewayIds={seedRequiredGatewayIds}
        onCancel={() => setSeedRequiredGatewayIds(null)}
        onSubmit={() => {
          setSeedRequiredGatewayIds(null);
          setSeedDestinations({});
          seedEntries();
        }}
      />
      <GatewayDestinationsModal
        siteId={jobsiteId}
        gatewayIds={seedGatewayIds}
        zones={layout.zones}
        destinations={seedDestinations}
        onChange={(gatewayId, zoneId) =>
          setSeedDestinations((chosen) => ({
            ...chosen,
            [gatewayId]: zoneId,
          }))
        }
        error={seedError}
        busy={busy}
        onCancel={() => setSeedGatewayIds(null)}
        onSubmit={() =>
          seedEntries(
            (seedGatewayIds ?? []).map((gatewayId) => ({
              gatewayId,
              zoneId: seedDestinations[gatewayId],
            })),
          )
        }
      />
      <CannotRemoveZoneModal
        blocked={blockedZone}
        onClose={() => setBlockedZone(null)}
      />
      <DiscardDraftModal
        isOpen={openModal === 'discard'}
        discarding={busy}
        onCancel={() => setOpenModal(null)}
        onDiscard={discard}
      />
    </Container>
  );
}

/**
 * The designer for a site where its flag is on and the site's permission
 * catalog lets the user view the layout; otherwise it says so. Nothing is
 * decided while the catalog loads.
 */
function SiteLayoutDesignerGate(props: RouteParams): React.ReactElement {
  const { jobsiteId } = props;
  const { siteLayoutUiEnabled } = useFlags();
  const permissions = useSitePermissions(jobsiteId);
  if (permissions.loading) return <LoadingError loading />;
  if (!siteLayoutUiEnabled || !permissions.can(SitePermission.ViewSiteLayout))
    return (
      <Container className={classes.container}>
        <p className={classes.denied}>
          You do not have access to this site&apos;s layout.
        </p>
      </Container>
    );
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SiteLayoutDesigner {...props} />;
}

/**
 * The site layout designer on its own page, reached from the versions list
 * on the Site Layout tab. The route's version is the draft, the effective
 * revision (`current`) or a revision by id; the draft is the only one that
 * can be edited, and only by a user who may manage the site's layout. Zones
 * and entries are tabs, as the site's settings are.
 */
export function SiteLayoutDesignerContainer(): React.ReactElement {
  const { jobsiteId, version, tab } = useParams<RouteParams>();
  return (
    // Keyed by site, so one site's flags and permissions never decide
    // another's designer while the next site's load.
    <JobsiteFeatureFlagsProvider key={jobsiteId} jobsiteId={jobsiteId}>
      <SiteLayoutDesignerGate
        jobsiteId={jobsiteId}
        version={version}
        tab={tab}
      />
    </JobsiteFeatureFlagsProvider>
  );
}
